import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AppContext } from '@/contexts/AppContext';
import type { Product, SpecialOffer } from '@/types';
import { ProductService } from '@/services/domain/ProductService';
import type { Category as ApiCategory, Product as ApiProduct, ProductFilters } from '@/types/api';

import carrinhoImg from '@/assets/images/carrinho.png';
import { OffersCarousel } from '@/components/OffersCarousel/OffersCarousel';

const MAX_PER_PAGE = 100;
type PerPageOption = number | 'all';
type SortOptionValue = 'name_asc' | 'name_desc' | 'price_asc' | 'price_desc';

const PER_PAGE_OPTIONS: Array<{ value: PerPageOption; label: string }> = [
  { value: 10, label: '10' },
  { value: 20, label: '20' },
  { value: 30, label: '30' },
  { value: 40, label: '40' },
  { value: 50, label: '50' },
  { value: 100, label: '100' },
  { value: 'all', label: 'Todos' },
];

const SORT_OPTIONS: Array<{
  value: SortOptionValue;
  label: string;
  sortBy: 'name' | 'price_cents';
  sortOrder: 'asc' | 'desc';
}> = [
  { value: 'name_asc', label: 'A–Z', sortBy: 'name', sortOrder: 'asc' },
  { value: 'name_desc', label: 'Z–A', sortBy: 'name', sortOrder: 'desc' },
  { value: 'price_asc', label: 'Preço: menor → maior', sortBy: 'price_cents', sortOrder: 'asc' },
  { value: 'price_desc', label: 'Preço: maior → menor', sortBy: 'price_cents', sortOrder: 'desc' },
];

const formatCurrency = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const CartIcon = () => (
  <img 
    src={carrinhoImg} 
    alt="Carrinho" 
    width="22" 
    height="22"
    style={{ display: 'block' }}
  />
);

export const CheckIcon = () => (
  <svg
    aria-hidden="true"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const splitPriceLabel = (rawPrice: string) => {
  if (!rawPrice) {
    return { currency: 'R$', amount: '0,00' };
  }

  const match = rawPrice.trim().match(/^([^0-9,]+)?\s*(.+)$/);
  if (!match) {
    return { currency: 'R$', amount: rawPrice.trim() };
  }

  const currency = match[1]?.trim() || 'R$';
  const amount = match[2]?.trim() || '0,00';
  return { currency, amount };
};

const CatshopPage = () => {
  const context = useContext(AppContext);
  
  if (!context) {
    throw new Error('CatshopPage must be used within an AppProvider');
  }
  
  const {
    selectedCategory,
    setSelectedCategory,
    addToCart,
    setIsCartOpen,
    specialOffers,
    calculateDiscountedPrice,
    products: contextProducts,
  } = context;

  const [catalogProducts, setCatalogProducts] = useState<Product[]>([]);
  const [apiCategories, setApiCategories] = useState<ApiCategory[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string>('');
  const [addedProducts, setAddedProducts] = useState<Record<number, boolean>>({});
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<PerPageOption>(PER_PAGE_OPTIONS[0].value);
  const [sortOption, setSortOption] = useState<SortOptionValue>('name_asc');
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);

  const [paginationInfo, setPaginationInfo] = useState<{ currentPage: number; lastPage: number; total: number }>({
    currentPage: 1,
    lastPage: 1,
    total: 0,
  });
  const [searchTerm, setSearchTerm] = useState('');

  const normalizedSearchTerm = useMemo(() => searchTerm.trim(), [searchTerm]);

  const feedbackTimersRef = useRef<Record<number, number>>({});
  const sortDropdownRef = useRef<HTMLDivElement | null>(null);

  const categoriesById = useMemo(() => {
    return apiCategories.reduce((map, category) => map.set(category.id, category), new Map<number, ApiCategory>());
  }, [apiCategories]);

  const selectedSortConfig = useMemo(
    () => SORT_OPTIONS.find(option => option.value === sortOption) ?? SORT_OPTIONS[0],
    [sortOption],
  );

  const categorySlugByName = useMemo(() => {
    return apiCategories.reduce((map, category) => map.set(category.name, category.slug), new Map<string, string>());
  }, [apiCategories]);

  const categories = useMemo(() => ['Todos', ...apiCategories.map(category => category.name)], [apiCategories]);

  const mapApiProduct = useCallback((apiProduct: ApiProduct): Product => {
    const categoryName = apiProduct.category_id ? categoriesById.get(apiProduct.category_id)?.name ?? 'Outros' : 'Outros';
    const priceCents = apiProduct.price_cents ?? 0;
    const originalCents = apiProduct.original_price_cents ?? priceCents;
    const hasDiscount = originalCents > priceCents;
    const discountPercentage = hasDiscount
      ? Math.round(((originalCents - priceCents) / originalCents) * 100)
      : 0;

    return {
      id: apiProduct.id,
      name: apiProduct.name,
      description: apiProduct.description ?? '',
      image: '🐾',
      imageUrl: apiProduct.image_url,
      price: apiProduct.price_formatted ?? formatCurrency(priceCents / 100),
      category: categoryName,
      priceNumber: apiProduct.price_reais ?? priceCents / 100,
      inStock: apiProduct.is_available ?? (apiProduct.stock_quantity ?? 0) > 0,
      discount: hasDiscount ? discountPercentage : undefined,
      isSpecialOffer: apiProduct.offer_applied ?? false,
      originalPrice: hasDiscount ? originalCents / 100 : undefined,
      appliedDiscount: hasDiscount ? discountPercentage : undefined,
    };
  }, [categoriesById]);

  useEffect(() => {
    let active = true;

    const fetchCategories = async () => {
      try {
        const data = await ProductService.categories();
        if (!active) return;
        setApiCategories(data);
      } catch (err) {
        console.error('Erro ao carregar categorias', err);
        if (!active) return;
        setApiCategories([]);
      }
    };

    fetchCategories();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!isSortMenuOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target as Node)) {
        setIsSortMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSortMenuOpen]);

  useEffect(() => {
    let active = true;

    const fetchProducts = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const filters: ProductFilters = {
          active_only: true,
          sort_by: selectedSortConfig.sortBy,
          sort_order: selectedSortConfig.sortOrder,
          search: normalizedSearchTerm || undefined,
        };

        const shouldFetchAll = perPage === 'all';

        if (selectedCategory !== 'Todos') {
          const categorySlug = categorySlugByName.get(selectedCategory);
          if (categorySlug) {
            filters.category = categorySlug;
          }
        }

        if (shouldFetchAll) {
          const allProducts = await ProductService.listAll({
            ...filters,
            per_page: MAX_PER_PAGE,
          });
          if (!active) return;

          const mapped = allProducts.map((item: ApiProduct) => mapApiProduct(item));
          setCatalogProducts(mapped);
          setPaginationInfo({
            currentPage: 1,
            lastPage: 1,
            total: mapped.length,
          });
        } else {
          const pagination = await ProductService.list({
            ...filters,
            page,
            per_page: Math.min(perPage, MAX_PER_PAGE),
          });
          if (!active) return;

          const data = Array.isArray(pagination.data) ? pagination.data : [];
          const mapped = data.map((item: ApiProduct) => mapApiProduct(item));
          setCatalogProducts(mapped);
          const currentPageFromApi = typeof pagination.current_page === 'number' ? pagination.current_page : page;
          const lastPageFromApi = typeof pagination.last_page === 'number' ? pagination.last_page : currentPageFromApi;
          const totalFromApi = typeof pagination.total === 'number' ? pagination.total : mapped.length;
          setPaginationInfo({
            currentPage: currentPageFromApi,
            lastPage: Math.max(1, lastPageFromApi),
            total: Math.max(0, totalFromApi),
          });
        }
      } catch (err) {
        console.error('Erro ao carregar produtos', err);
        if (!active) return;
        setError('Não foi possível carregar os produtos no momento.');
        setCatalogProducts([]);

        setPaginationInfo({
          currentPage: 1,
          lastPage: 1,
          total: 0,
        });
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    };

    fetchProducts();

    return () => {
      active = false;
    };
  }, [
    selectedCategory,
    categorySlugByName,
    mapApiProduct,
    page,
    perPage,
    selectedSortConfig,
    normalizedSearchTerm,
  ]);

  useEffect(() => {
    setPage(1);
  }, [normalizedSearchTerm]);

  const activeOffers = useMemo(() => {
    const now = new Date();
    return specialOffers.filter((offer: SpecialOffer) => {
      const start = offer.startDate ? new Date(offer.startDate) : null;
      const end = offer.endDate ? new Date(offer.endDate) : null;
      const isStartOk = !start || (!Number.isNaN(start.getTime()) && start <= now);
      const isEndOk = !end || (!Number.isNaN(end.getTime()) && end >= now);
      return offer.isActive && isStartOk && isEndOk;
    });
  }, [specialOffers]);

  const offerProducts = useMemo(() => {
    const offerProductIds = new Set(activeOffers.map(offer => offer.productId));
    const sourceProducts = contextProducts.length > 0 ? contextProducts : catalogProducts;

    return sourceProducts.filter(product => {
      if (product.isSpecialOffer) return true;
      if ((product.discount ?? 0) > 0) return true;
      return offerProductIds.has(product.id);
    });
  }, [activeOffers, catalogProducts, contextProducts]);

  const isOffersLoading = contextProducts.length === 0 && isLoading;

  const filteredProducts = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    const filtered = catalogProducts.filter(product => {
      const matchesCategory = selectedCategory === 'Todos' || product.category === selectedCategory;
      const matchesSearch = normalizedSearch.length === 0 || product.name.toLowerCase().includes(normalizedSearch);
      return matchesCategory && matchesSearch;
    });

    return [...filtered].sort((productA, productB) => {
      if (selectedSortConfig.sortBy === 'name') {
        const nameA = productA.name.toLowerCase();
        const nameB = productB.name.toLowerCase();
        if (nameA < nameB) return selectedSortConfig.sortOrder === 'asc' ? -1 : 1;
        if (nameA > nameB) return selectedSortConfig.sortOrder === 'asc' ? 1 : -1;
        return 0;
      }

      if (selectedSortConfig.sortBy === 'price_cents') {
        const priceA = productA.priceNumber ?? 0;
        const priceB = productB.priceNumber ?? 0;
        if (priceA < priceB) return selectedSortConfig.sortOrder === 'asc' ? -1 : 1;
        if (priceA > priceB) return selectedSortConfig.sortOrder === 'asc' ? 1 : -1;
        return 0;
      }

      return 0;
    });
  }, [catalogProducts, searchTerm, selectedCategory, selectedSortConfig]);

  const getPriceInfo = useCallback((product: Product) => {
    if (product.originalPrice && product.discount && product.discount > 0) {
      return {
        original: formatCurrency(product.originalPrice),
        final: product.price,
        discount: product.discount,
      };
    }

    if (product.discount && product.discount > 0) {
      return {
        original: product.price,
        final: calculateDiscountedPrice(product.price, product.discount),
        discount: product.discount,
      };
    }

    return {
      original: undefined,
      final: product.price,
      discount: undefined,
    };
  }, [calculateDiscountedPrice]);

  const renderProductImage = useCallback((product: Product) => {
    if (product.imageUrl) {
      return <img src={product.imageUrl} alt={product.name} loading="lazy" />;
    }
    return <span>{product.image}</span>;
  }, []);

  useEffect(() => {
    return () => {
      Object.values(feedbackTimersRef.current).forEach(timerId => {
        window.clearTimeout(timerId);
      });
    };
  }, []);

  const handleCategorySelection = useCallback((category: string) => {
    setSelectedCategory(category);
    setPage(1);
  }, [setSelectedCategory]);

  const handlePerPageChange = useCallback((value: PerPageOption) => {
    if (value === 'all') {
      setPerPage('all');
    } else {
      const numericValue = Number(value) || (PER_PAGE_OPTIONS[0].value as number);
      const clampedValue = Math.min(Math.max(1, numericValue), MAX_PER_PAGE);
      setPerPage(clampedValue);
    }
    setPage(1);
  }, []);

  const handleSortChange = useCallback((value: SortOptionValue) => {
    setSortOption(value);
    setPage(1);
    setIsSortMenuOpen(false);
  }, []);

  const handleAddToCart = useCallback(
    (product: Product) => {
      if (product.inStock === false) {
        return;
      }

      addToCart(product);
      setAddedProducts(prev => ({
        ...prev,
        [product.id]: true,
      }));

      if (feedbackTimersRef.current[product.id]) {
        window.clearTimeout(feedbackTimersRef.current[product.id]);
      }

      feedbackTimersRef.current[product.id] = window.setTimeout(() => {
        setAddedProducts(prev => {
          const updated = { ...prev };
          delete updated[product.id];
          return updated;
        });
        delete feedbackTimersRef.current[product.id];
      }, 1600);

      setFeedbackMessage(`${product.name} adicionado ao carrinho.`);
    },
    [addToCart],
  );

  const selectedProductPriceInfo = selectedProduct ? getPriceInfo(selectedProduct) : null;
  const selectedProductPriceParts = selectedProductPriceInfo
    ? splitPriceLabel(selectedProductPriceInfo.final)
    : null;
  const { currentPage, lastPage, total } = paginationInfo;
  const safeLastPage = Math.max(1, lastPage);
  const showPaginationControls = perPage !== 'all' && !isLoading && filteredProducts.length > 0 && safeLastPage > 1;

  return (
    <section className="catshop">
      <div className="container">

        <h2>Catshop - Produtos Exclusivos</h2>
        <p className="section-subtitle">Tudo que seu gatinho precisa em um só lugar</p>

        {/* Carrossel de ofertas especiais - ativa modo carrossel se houver mais de 6 produtos */}
        <OffersCarousel
          products={offerProducts}
          activeOffers={activeOffers}
          calculateDiscountedPrice={calculateDiscountedPrice}
          onAddToCart={handleAddToCart}
          addedProducts={addedProducts}
          renderProductImage={renderProductImage}
          getPriceInfo={getPriceInfo}
          isLoading={isOffersLoading}
          onProductClick={setSelectedProduct}
        />

        <div className="catshop-toolbar">
          <div className="catshop-toolbar-row catshop-toolbar-row--categories">
            <div className="category-filters">
              {categories.map(category => (
                <button
                  key={category}
                  className={selectedCategory === category ? 'active' : ''}
                  onClick={() => handleCategorySelection(category)}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          <div className="catshop-toolbar-row catshop-toolbar-row--controls">
            <div className="product-search-field">
              <label htmlFor="product-search-input">Nome do Produto</label>
              <input
                type="text"
                id="product-search-input"
                value={searchTerm}
                onChange={event => setSearchTerm(event.target.value)}
                placeholder="Filtrar por nome"
              />
            </div>

            <div className="sort-control">
              <label htmlFor="product-sort-select">Ordenar</label>
              <div
                className={`sort-dropdown ${isSortMenuOpen ? 'open' : ''}`}
                ref={sortDropdownRef}
              >
                <button
                  type="button"
                  id="product-sort-select"
                  className="sort-dropdown__button"
                  disabled={isLoading}
                  aria-haspopup="listbox"
                  aria-expanded={isSortMenuOpen}
                  onClick={() => setIsSortMenuOpen(prev => !prev)}
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
                      event.preventDefault();
                      setIsSortMenuOpen(prev => !prev);
                    }
                    if (event.key === 'Escape') {
                      setIsSortMenuOpen(false);
                    }
                  }}
                >
                  <span>{selectedSortConfig.label}</span>
                  <span aria-hidden="true" className="sort-dropdown__chevron" />
                </button>

                {isSortMenuOpen && (
                  <ul
                    className="sort-dropdown__menu"
                    role="listbox"
                    aria-activedescendant={sortOption}
                  >
                    {SORT_OPTIONS.map(option => {
                      const isSelected = sortOption === option.value;
                      return (
                        <li key={option.value}>
                          <button
                            type="button"
                            role="option"
                            aria-selected={isSelected}
                            className={`sort-dropdown__option ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleSortChange(option.value)}
                          >
                            {option.label}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>

            <div className="per-page-control" role="group" aria-label="Itens por página">
              <div className="per-page-options">
                {PER_PAGE_OPTIONS.map(option => {
                  const isActive = perPage === option.value;
                  return (
                    <button
                      key={option.value ?? option.label}
                      type="button"
                      className={`per-page-option ${isActive ? 'active' : ''}`}
                      onClick={() => handlePerPageChange(option.value)}
                      disabled={isLoading}
                      aria-pressed={isActive}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div className="products-grid">
          {isLoading ? (
            <div className="loading-state">Carregando produtos...</div>
          ) : error ? (
            <div className="error-state">{error}</div>
          ) : filteredProducts.length === 0 ? (
            <div className="empty-state">Nenhum produto encontrado nesta categoria.</div>
          ) : (
            filteredProducts.map(product => {
              const priceInfo = getPriceInfo(product);

              const currentPriceLabel = priceInfo.final;
              const priceParts = splitPriceLabel(currentPriceLabel);

              return (
                <div key={product.id} className={`product-card ${!(product.inStock ?? true) ? 'out-of-stock' : ''}`}>
                  <div className="product-media">
                    <div className="product-media__frame">
                      <div
                        className="product-media__content"
                        onClick={() => setSelectedProduct(product)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setSelectedProduct(product);
                          }
                        }}
                      >
                        {renderProductImage(product)}
                      </div>
                    </div>
                  </div>

                  <div className="product-body">
                    <h3 className="product-title">{product.name}</h3>
                    <p className="product-description">{product.description}</p>
                  </div>
                  <div className="product-footer">
                    <div className="product-price">
                      <div className="product-price__current">
                        <span className="product-price__currency">{priceParts.currency}</span>
                        <span className="product-price__amount">{priceParts.amount}</span>
                      </div>
                      {(priceInfo.original || priceInfo.discount) && (
                        <div className="product-price-details">
                          {priceInfo.original && (
                            <span className="original-price">{priceInfo.original}</span>
                          )}
                          {priceInfo.discount && (
                            <span className="discount-badge">-{priceInfo.discount}%</span>
                          )}
                        </div>
                      )}
                    </div>
                    {!(product.inStock ?? true) && (
                      <div className="out-of-stock-badge">❌ Sem Estoque</div>
                    )}
                    <div className="product-actions">
                      <button
                        onClick={() => handleAddToCart(product)}
                        className={`add-to-cart-button ${addedProducts[product.id] ? 'added' : ''}`}
                        disabled={product.inStock === false}
                        aria-label={addedProducts[product.id]
                          ? `Produto ${product.name} adicionado.`
                          : `Adicionar ${product.name} ao carrinho`
                        }
                        type="button"
                      >
                        <span className="button-content">
                          {addedProducts[product.id] ? <CheckIcon /> : <CartIcon />}
                          <span className="button-label" aria-live="polite">
                            {product.inStock !== false ? (addedProducts[product.id] ? 'Adicionado' : 'Adicionar ao Carrinho') : 'Indisponível'}
                          </span>
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {showPaginationControls && (
          <div className="pagination-controls" aria-live="polite">
            <button
              type="button"
              onClick={() => setPage(prev => (prev <= 1 ? prev : prev - 1))}
              disabled={isLoading || currentPage <= 1}
            >
              Anterior
            </button>
            <div className="pagination-info">
              <span>
                Página {currentPage} de {safeLastPage}
              </span>
              <span className="pagination-total">
                {total} {total === 1 ? 'produto' : 'produtos'}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setPage(prev => (prev >= safeLastPage ? prev : prev + 1))}
              disabled={isLoading || currentPage >= safeLastPage}
            >
              Próxima
            </button>
          </div>
        )}
      </div>
      <div className="sr-only" role="status" aria-live="polite">
        {feedbackMessage}
      </div>

      {selectedProduct && (
        <div className="product-modal-overlay" onClick={() => setSelectedProduct(null)}>
          <div
            className="product-modal"
            onClick={event => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={selectedProduct.name}
          >
            <button
              type="button"
              className="product-modal__close-button"
              onClick={() => setSelectedProduct(null)}
              aria-label="Fechar detalhes do produto"
            >
              ×
            </button>

            <div className="product-modal__content">
              <div className="product-modal__image">
                {renderProductImage(selectedProduct)}
              </div>

              <div className="product-modal__info">
                <h3 className="product-modal__title">{selectedProduct.name}</h3>
                {selectedProduct.description && (
                  <p className="product-modal__description">{selectedProduct.description}</p>
                )}

                {selectedProductPriceInfo && selectedProductPriceParts && (
                  <div className="product-price product-modal__price">
                    <div className="product-price__current">
                      <span className="product-price__currency">
                        {selectedProductPriceParts.currency}
                      </span>
                      <span className="product-price__amount">
                        {selectedProductPriceParts.amount}
                      </span>
                    </div>

                    {(selectedProductPriceInfo.original || selectedProductPriceInfo.discount) && (
                      <div className="product-price-details">
                        {selectedProductPriceInfo.original && (
                          <span className="original-price">
                            {selectedProductPriceInfo.original}
                          </span>
                        )}
                        {selectedProductPriceInfo.discount && (
                          <span className="discount-badge">
                            -{selectedProductPriceInfo.discount}%
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="product-actions product-modal__actions">
                  <button
                    onClick={() => handleAddToCart(selectedProduct)}
                    className={`add-to-cart-button ${addedProducts[selectedProduct.id] ? 'added' : ''}`}
                    disabled={selectedProduct.inStock === false}
                    aria-label={addedProducts[selectedProduct.id]
                      ? `Produto ${selectedProduct.name} adicionado.`
                      : `Adicionar ${selectedProduct.name} ao carrinho`
                    }
                    type="button"
                  >
                    <span className="button-content">
                      {addedProducts[selectedProduct.id] ? <CheckIcon /> : <CartIcon />}
                      <span className="button-label" aria-live="polite">
                        {selectedProduct.inStock !== false
                          ? (addedProducts[selectedProduct.id] ? 'Adicionado' : 'Adicionar ao Carrinho')
                          : 'Indisponível'}
                      </span>
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default CatshopPage;